import { useEffect } from 'react';

interface HttpsRedirectProps {
    enabled?: boolean;
    excludeLocalhost?: boolean;
    forceInProduction?: boolean;
}

/**
 * Component that automatically redirects HTTP traffic to HTTPS.
 * Only enforces protocol. Authentication/UI redirects are handled in routes.tsx.
 */
export const HttpsRedirect: React.FC<HttpsRedirectProps> = ({
    enabled = true,
    excludeLocalhost = false,
    forceInProduction = true
}) => {
    useEffect(() => {
        if (!enabled) return;

        const currentProtocol = window.location.protocol;
        const hostname = window.location.hostname;
        const isLocalhost =
            hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

        // Skip redirect for localhost if excludeLocalhost is true
        if (excludeLocalhost && isLocalhost) {
            return;
        }

        // Check if we're on HTTP and should redirect to HTTPS
        const shouldRedirect =
            currentProtocol === 'http:' &&
            (forceInProduction ||
                import.meta.env.MODE === 'production' ||
                import.meta.env.MODE === 'staging' ||
                import.meta.env.VITE_FORCE_HTTPS === 'true');

        if (shouldRedirect) {
            const httpsUrl = window.location.href.replace('http://', 'https://');
            // Use replace to avoid creating a back button entry
            window.location.replace(httpsUrl);
        }
    }, [enabled, excludeLocalhost, forceInProduction]);

    return null; // This component doesn't render anything
};

/**
 * Utility function to get the current URL with HTTPS protocol
 */
export const getHttpsUrl = (url?: string): string => {
    const targetUrl = url || window.location.href;
    return targetUrl.replace(/^http:\/\//, 'https://');
};

/**
 * Utility function to check if HTTPS is available
 */
export const checkHttpsAvailable = async (url?: string): Promise<boolean> => {
    try {
        const httpsUrl = getHttpsUrl(url);
        await fetch(httpsUrl, {
            method: 'HEAD',
            mode: 'no-cors' // Avoid CORS issues for simple availability check
        });
        return true;
    } catch {
        return false;
    }
};

export default HttpsRedirect;